/**
 * plan-grouping.js — collapse carrier-returned variants of the same plan
 * into one group so the card can switch deductible/coinsurance tiers
 * instantly from already-fetched data. Pure functions, no DOM.
 */

const COINS_RE = /\b(\d{2,3}\/\d{1,3})\b|\b(\d{1,3}\s?%)/; // "80/20" or "70%" — for .match()
const COINS_RE_G = /\b(\d{2,3}\/\d{1,3})\b|\b(\d{1,3}\s?%)/g; // global — for .replace()
const MONEY_RE = /\$\s?[\d,]+/g;
const NETWORK_RE = /\b(PPO|EPO|HMO|POS)\b(\s*\d+)?/i;  // network token, e.g. "PPO" / "EPO 2"
const NETWORK_RE_G = /\b(PPO|EPO|HMO|POS)\b(\s*\d+)?/ig;
const HSA_RE = /\bHSA\b/i;
const TIER_RE = /\b(Classic|HSA|Value)\b/i; // LifeX MM plan tiers
const TIER_CANON = { classic: 'Classic', hsa: 'HSA', value: 'Value' };
const TARGET_DEDUCTIBLE = 5000; // mid-tier default ("typically the $5,000 deductible")

// Products whose variants fragment under name-derivation. Each rule is scoped to an exact
// carrier + name prefix → only these products are collapsed; everything else is untouched.
// tier(name) returns the variant-axis label, or null when the deductible/network axes already
// distinguish the variants (e.g. Pivot Core differs only by deductible).
const GROUPING_RULES = [
  { carrier: 'PHCS', name: /^LifeX MM\b/i, family: 'LifeX MM',
    tier: (n) => TIER_CANON[((n.match(TIER_RE) || [])[1] || '').toLowerCase()] || null },
  { carrier: 'UHOne', name: /^Health ProtectorGuard\b/i, family: 'Health ProtectorGuard',
    // Full suffix (multi-word safe) between the product name and the end of the string, KEEPING
    // the trailing benefit number, e.g. "Health ProtectorGuard Elite Plus 6000" → "Elite Plus
    // 6000", "… Select 2000" → "Select 2000" — two generations sharing a tier word but a different
    // benefit number (e.g. "Preferred 1" vs "Preferred 4000") must stay distinct tiers (2026-09-10).
    tier: (n) => n.replace(/^Health ProtectorGuard\s+/i, '').trim() || null },
  // All Manhattan Life supplemental products are normalized as "<productName> - <planName>".
  // Group every Manhattan product by its productName and expose the planName — the benefit
  // amount / level (e.g. "$1,000 Benefit", "Classic", "Plan I", "ProGap") — as the variant axis,
  // so DVH, Affordable Choice, Cancer Care Plus, CHAS, Accident, Critical Illness, VisionWise,
  // etc. each collapse into ONE card with a benefit selector instead of fragmenting into a card
  // per benefit. (family is a function here — see deriveVariantAxes.)
  { carrier: 'Manhattan Life', name: /.*/,
    family: (n) => (String(n).split(' - ')[0] || String(n)).trim(),
    tier: (n) => (String(n).split(' - ').slice(1).join(' - ')).trim() || null },
  { carrier: 'Companion Life Insurance Company', name: /^Pivot Health Core\b/i, family: 'Pivot Health Core',
    tier: () => null },
  // No `display` — the card title follows the selected variant's own plan.name (deriveVariantAxes'
  // `display` plumbing yields null for these rules), so picking the 36-month row surfaces its own
  // name (e.g. "TriTerm Value") instead of a stale "Short Term Medical Value" title (2026-09-10).
  { carrier: 'UHOne', name: /^(Short Term Medical Value|TriTerm Value|Value)$/i,   family: 'UHOne:Value',     tier: () => null },
  { carrier: 'UHOne', name: /^(Short Term Medical Plus Elite|Plan 100 Max)$/i,     family: 'UHOne:PlusElite', tier: () => null },
  { carrier: 'UHOne', name: /^(Short Term Medical Copay|Copay Select Max)$/i,      family: 'UHOne:Copay',     tier: () => null },
  // UHOne Accident (ProGap/ProGuard) & Critical Illness: collapse rate-variants into ONE card
  // per product. The benefit dollar amount(s) ride the `tier` axis (via plan._benefitTier) and,
  // for ProGuard, a second `ciBenefitLevel` axis — both stamped in uhone-api.js normalize.
  // tier:()=>null here so the rule adds no competing tier; the _benefitTier override in
  // deriveVariantAxes supplies it. deductible is 0 for all these rates → deductible axis collapses.
  { carrier: 'UHOne', name: /^Accident ProGap\b/i,   family: 'Accident ProGap',   tier: () => null },
  { carrier: 'UHOne', name: /^Accident ProGuard\b/i, family: 'Accident ProGuard', tier: () => null },
  { carrier: 'UHOne', name: /^Critical Illness\b/i,  family: 'Critical Illness',  tier: () => null },
];

/**
 * Derive grouping axes for a plan.
 * @param {object} plan
 * @returns {{family:string, deductible:number, coinsurance:(string|null)}}
 */
export function deriveVariantAxes(plan) {
  if (!plan) return { family: '', deductible: 0, coinsurance: null, network: null, hsa: false, tier: null, term: null, display: null, ciBenefitLevel: null, chs: null, sip: null, rateLock: null, visionRider: null, savingsCard: null };
  const rawName = (plan && plan.name) ? String(plan.name) : '';
  const coinsMatch = rawName.match(COINS_RE);
  const coinsurance =
    plan.coinsurance != null ? String(plan.coinsurance)
    : plan.planDetails && plan.planDetails.coinsurance != null ? String(plan.planDetails.coinsurance)
    : coinsMatch ? (coinsMatch[1] || coinsMatch[2].replace(/\s/g, ''))
    : null;

  // Iron Health/PSM plans carry an explicit groupFamily like "Livelihood EPO 2" or "RBP HSA".
  // Split it into a BASE family (so all Livelihood / RBP / First Health … collapse into one
  // card) plus network (PPO/EPO/…) and HSA variant axes. Other carriers (no groupFamily) fall
  // back to the name-derived family with no network/HSA axes.
  let family;
  let network = null;
  let hsa = false;
  let tier = null;
  let display = null;
  const carrierId = (plan.carrier || plan.network) || '';
  const rule = GROUPING_RULES.find((r) => r.carrier === carrierId && r.name.test(rawName));
  if (rule) {
    family = typeof rule.family === 'function' ? rule.family(rawName) : rule.family;
    tier = rule.tier(rawName);
    display = rule.display || null;
    // Derive network from the name too (e.g. "Pivot Health Core 1000 PPO" → PPO) so that if a
    // rule-merged product ever returns multiple networks at one deductible they stay distinct
    // variants rather than colliding. No-op for products without a network token in the name.
    const nm = rawName.match(NETWORK_RE);
    network = nm ? nm[0].replace(/\s+/g, ' ').trim().toUpperCase() : null;
  } else if (plan.groupFamily) {
    const gf = String(plan.groupFamily);
    hsa = HSA_RE.test(gf);
    const nm = gf.match(NETWORK_RE);
    network = nm ? nm[0].replace(/\s+/g, ' ').trim().toUpperCase() : null;
    family = gf.replace(NETWORK_RE_G, ' ').replace(HSA_RE, ' ').replace(/\s{2,}/g, ' ').trim();
    if (!family) family = gf;
  } else {
    family = rawName
      .replace(MONEY_RE, ' ')
      .replace(COINS_RE_G, ' ')
      .replace(/\bdeductible\b/ig, ' ')
      .replace(/\s{2,}/g, ' ')
      .trim();
    // A carrier network that's a real member choice (e.g. Allstate STM: Aetna vs Cigna in many
    // states) becomes a variant axis. The carrier-name fallback (plan.network === the carrier id,
    // e.g. "Manhattan Life") is NOT a network. Single-network carriers yield one value → no chip.
    if (plan.network && plan.network !== carrierId) network = plan.network;
  }
  if (!family) family = String((plan && plan.id) != null ? plan.id : '');

  // UHOne Accident/Critical-Illness benefit chips (#1/#3): the normalizer stamps the benefit
  // dollar label onto _benefitTier (→ the `tier` axis) and, for ProGuard's 2-D matrix, the
  // second benefit onto _ciBenefitLevel (→ a dedicated ciBenefitLevel axis). Scoped: only plans
  // carrying these fields are affected; every other carrier is untouched.
  if (plan._benefitTier) tier = plan._benefitTier;
  const ciBenefitLevel = (plan._ciBenefitLevel != null && plan._ciBenefitLevel !== '') ? plan._ciBenefitLevel : null;

  const deductible = parseFloat(String(plan.deductible ?? 0).replace(/[$,]/g, '')) || 0;
  const term = (plan._termId && plan._termId !== 'na') ? String(plan._termId) : null;

  // Plan Enhancer CHS/SIP add-on axes (NGAH NHICAME): each carrier row is a fixed combo
  // (with/without CHS, with/without SIP) at its own price, so toggling = switching variants.
  // Scoped: only plans stamped with ameChs/ameSip get an axis value; everything else stays null.
  const pd = plan.planDetails || {};
  const chs = pd.ameChs != null ? !!pd.ameChs : null;
  const sip = pd.ameSip != null ? !!pd.ameSip : null;

  // Allstate STM renewal rate-lock: locked/non-locked renewal siblings are separately priced
  // rows differing only in stmRenewalRateLock. Scoped: non-null ONLY on renewal rows
  // (stmRenewalTerms > 0) — single-term/2x12 rows keep a null axis so findVariantByAxes'
  // null-tolerant eqLock lets a term switch away from a locked variant land on them.
  const rateLock = ((pd.stmRenewalTerms || 0) > 0 && pd.stmRenewalRateLock != null) ? !!pd.stmRenewalRateLock : null;

  // NGAH dental axes: Select Dental vision-rider level ('None'/'Level 1'/'Level 2') and Dental
  // Indemnity savings-card siblings — separately priced variants, not additive riders. Scoped:
  // only plans stamped in api.js normalize get a value; every other product stays null.
  const visionRider = (pd.dentalVisionRider != null && pd.dentalVisionRider !== '') ? String(pd.dentalVisionRider) : null;
  const savingsCard = pd.dentalSavingsCard != null ? !!pd.dentalSavingsCard : null;

  return { family, deductible, coinsurance, network, hsa, tier, term, display, ciBenefitLevel, chs, sip, rateLock, visionRider, savingsCard };
}

/**
 * Group-key type normalization — collapse LifeX MM's HSA tier into its Classic/Value siblings
 * so all "LifeX MM" plans merge into one card. SCOPED to the `LifeX MM` family so no other
 * carrier's `type:'hsa'` is ever coerced (defense against a future carrier emitting that token).
 */
export function normalizeGroupType(type, family, term) {
  if (family === 'LifeX MM' && type === 'hsa') return 'major_medical';
  // Only merge into a term-pair card when a term axis actually exists (2026-09-10): legacy saved
  // quotes (pre-full-menu) carry no termMonths data, so term is null — without this guard they'd
  // merge into one termless stm-termpair group where same-deductible rows collide and only the
  // cheaper one is ever reachable. term == null (not === null) also covers the omitted-arg case.
  if (/^UHOne:/.test(family) && (type === 'short-term' || type === 'long-term') && term != null) return 'stm-termpair';
  return type;
}

// Term-axis ordering. Plain ids are month counts ('12' < '24' < '36'); consecutive-terms ids
// ('2x12') sort by total months nudged just before the same-length renewal product, so the
// chip row reads 12 · 2x12 · 24 · 36.
function termOrderKey(t) {
  const m = /^(\d+)x(\d+)$/.exec(String(t));
  return m ? Number(m[1]) * Number(m[2]) - 0.5 : Number(t);
}

// Vision-rider axis ordering: None first, then by level number ('Level 1' < 'Level 2').
function visionRiderOrderKey(v) {
  return v === 'None' ? 0 : (parseInt(String(v).replace(/\D/g, ''), 10) || 99);
}

/**
 * Group plans into variant families. Safe-by-default: only collapses plans
 * sharing carrier + type + normalized family name.
 * @param {Array<object>} plans
 * @returns {Array<object>} groups
 */
export function groupPlanVariants(plans) {
  const byKey = new Map();
  for (const plan of (plans || [])) {
    if (!plan) continue;
    const { family, deductible, coinsurance, network, hsa, tier, term, display, ciBenefitLevel, chs, sip, rateLock, visionRider, savingsCard } = deriveVariantAxes(plan);
    const carrier = plan.carrier || plan.network || 'LifeX';
    const type = normalizeGroupType(plan.type || plan.category || '', family, term);
    const key = carrier + '|' + type + '|' + family;
    const variant = {
      planId: String(plan.id),
      deductible,
      coinsurance,
      network,
      hsa,
      tier,
      term,
      display,
      ciBenefitLevel,
      chs,
      sip,
      rateLock,
      visionRider,
      savingsCard,
      premium: Number(plan.calculatedPremium) || 0,
      plan,
    };
    if (!byKey.has(key)) {
      byKey.set(key, { key, carrier, type, family, variants: [] });
    }
    byKey.get(key).variants.push(variant);
  }

  const groups = [];
  for (const group of byKey.values()) {
    group.variants.sort((a, b) => a.premium - b.premium);
    // Default to the mid-tier (~$5,000 deductible), preferring non-HSA then cheaper — not the
    // skimpiest/cheapest tier. This is also the variant shown for the featured plan.
    // When the group has a tier axis (LifeX MM: Classic/HSA/Value), prefer the "Classic" tier
    // first so the card opens on the standard non-HSA plan; the nearest-$5,000 pick then happens
    // within Classic. tierRank is a no-op for carriers without tiers (all tier null → equal).
    const tierRank = (t) => (t === 'Classic' ? 0 : 1);
    // stm-termpair (UHOne Value/PlusElite/Copay pairs): default to the SHORTEST term first — the
    // 12-month product — before the existing nearest-$5,000/non-HSA/cheapest tiebreak, so a
    // cheaper 36-month TriTerm row at the target deductible never wins the default away from its
    // 12-month sibling on price alone. No other group type's default changes (2026-09-10).
    const termPref = (v) => (v.term == null ? Number.POSITIVE_INFINITY : termOrderKey(v.term));
    group.defaultVariantId = group.variants.slice().sort((a, b) => {
      if (group.type === 'stm-termpair') {
        // Infinity - Infinity = NaN when BOTH variants have a null term; a NaN comparator return
        // makes Array.prototype.sort treat every subsequent pair as "equal" too, silently
        // disabling every tiebreak below (2026-09-10 review finding). Compare the two term
        // preferences directly instead of subtracting them.
        const ta = termPref(a), tb = termPref(b);
        if (ta !== tb) return ta < tb ? -1 : 1;
      }
      const ta = tierRank(a.tier), tb = tierRank(b.tier);
      if (ta !== tb) return ta - tb;
      const da = Math.abs(a.deductible - TARGET_DEDUCTIBLE);
      const db = Math.abs(b.deductible - TARGET_DEDUCTIBLE);
      if (da !== db) return da - db;
      if (!!a.hsa !== !!b.hsa) return a.hsa ? 1 : -1;
      return a.premium - b.premium;
    })[0].planId;
    // Livelihood (Iron Health): the agency's standard default is the PPO non-HSA $4,300 plan,
    // not the generic nearest-$5,000 pick. Scoped to the Iron Health Livelihood family so no
    // other carrier/product is affected; falls through to the generic default when that exact
    // variant wasn't returned for the quote.
    if (group.family === 'Livelihood' && group.carrier === 'Iron Health') {
      const liv = group.variants.find(v => v.deductible === 4300 && v.network === 'PPO' && !v.hsa);
      if (liv) group.defaultVariantId = liv.planId;
    }
    // Small-dollar supplemental cards (NGAH accident/dental families): every deductible sits far
    // below TARGET_DEDUCTIBLE, so the nearest-$5,000 major-medical default can open the card on a
    // pricier higher-deductible row (AcciMed's $100/$250 mix — audit 2026-08-05). Default to the
    // cheapest variant instead. Scoped to Allstate Health groups with uniformly sub-$1,000
    // deductibles; major-medical products (STM deductibles ≥ $2,500) keep the mid-tier default.
    if (group.carrier === 'Allstate Health' && group.variants.every(v => v.deductible < 1000)) {
      group.defaultVariantId = group.variants[0].planId; // variants are premium-sorted ascending
    }
    group.deductibles = [...new Set(group.variants.map(v => v.deductible))].sort((a, b) => a - b);
    group.coinsurances = [...new Set(group.variants.map(v => v.coinsurance).filter(Boolean))];
    group.networks = [...new Set(group.variants.map(v => v.network).filter(Boolean))];
    group.hsaOptions = [...new Set(group.variants.map(v => !!v.hsa))];
    group.tiers = [...new Set(group.variants.map(v => v.tier).filter(Boolean))];
    // ProGuard's second benefit axis (#1/#3); empty for every other product → no second chip row.
    group.ciBenefitLevels = [...new Set(group.variants.map(v => v.ciBenefitLevel).filter(Boolean))];
    // Plan Enhancer CHS/SIP toggle axes; empty for every other product → no toggle chips.
    group.chsOptions = [...new Set(group.variants.map(v => v.chs).filter(v => v != null))];
    group.sipOptions = [...new Set(group.variants.map(v => v.sip).filter(v => v != null))];
    // STM renewal rate-lock toggle options; empty for groups without renewal rows → no chip.
    group.rateLockOptions = [...new Set(group.variants.map(v => v.rateLock).filter(v => v != null))];
    // Select Dental vision-rider pills (None → Level 1 → Level 2); empty for every other product.
    group.visionRiderOptions = [...new Set(group.variants.map(v => v.visionRider).filter(v => v != null))].sort((a, b) => visionRiderOrderKey(a) - visionRiderOrderKey(b));
    // Dental Indemnity savings-card toggle options; empty for groups without the axis.
    group.savingsCardOptions = [...new Set(group.variants.map(v => v.savingsCard).filter(v => v != null))];
    group.terms = [...new Set(group.variants.map(v => v.term).filter(Boolean))].sort((a, b) => termOrderKey(a) - termOrderKey(b));
    group.displayName = group.variants.map(v => v.display).find(Boolean) || null;
    group.representative = group.variants[0].plan;
    groups.push(group);
  }
  return groups;
}

/**
 * Resolve the best variant for a desired axis combination, with progressive fallback so a
 * chip click never dead-ends: exact (deductible+coinsurance+network+hsa+rateLock) → drop
 * coinsurance → same network+hsa at nearest deductible → same network at nearest deductible.
 * rateLock is null-tolerant on BOTH sides: null-axis variants (single 12-month, 2x12) satisfy
 * any rateLock target, so a term switch away from a locked renewal variant never dead-ends.
 * @param {object} group
 * @param {{deductible:number, coinsurance?:(string|null), network?:(string|null), hsa?:boolean}} target
 * @returns {object|null}
 */
export function findVariantByAxes(group, target) {
  if (!group || !Array.isArray(group.variants)) return null;
  const vs = group.variants;
  const eqNet = (v) => target.network == null || v.network === target.network;
  const eqHsa = (v) => target.hsa == null || !!v.hsa === !!target.hsa;
  const eqCoins = (v) => target.coinsurance == null || v.coinsurance === target.coinsurance;
  const eqTier = (v) => target.tier == null || v.tier === target.tier;
  const eqTerm = (v) => target.term == null || v.term === target.term;
  const eqCiB = (v) => target.ciBenefitLevel == null || v.ciBenefitLevel === target.ciBenefitLevel;
  // Plan Enhancer CHS/SIP toggles — null target = axis not in play (every non-AME product).
  const eqChs = (v) => target.chs == null || v.chs === target.chs;
  const eqSip = (v) => target.sip == null || v.sip === target.sip;
  // STM renewal rate-lock — deliberately asymmetric vs eqChs: v.rateLock == null ALSO passes,
  // because one group mixes null-axis variants (12-month, 2x12) with true/false renewal ones.
  const eqLock = (v) => target.rateLock == null || v.rateLock == null || v.rateLock === target.rateLock;
  // NGAH dental axes — strict like eqChs: null target = axis not in play (every non-dental product).
  const eqVis = (v) => target.visionRider == null || v.visionRider === target.visionRider;
  const eqSav = (v) => target.savingsCard == null || v.savingsCard === target.savingsCard;
  const nearest = (list) => list.reduce((best, v) =>
    Math.abs(v.deductible - target.deductible) < Math.abs(best.deductible - target.deductible) ? v : best);

  let m = vs.find((v) => v.deductible === target.deductible && eqCoins(v) && eqNet(v) && eqHsa(v) && eqTier(v) && eqTerm(v) && eqCiB(v) && eqChs(v) && eqSip(v) && eqLock(v) && eqVis(v) && eqSav(v));
  if (m) return m;
  m = vs.find((v) => v.deductible === target.deductible && eqNet(v) && eqHsa(v) && eqTier(v) && eqTerm(v) && eqCiB(v) && eqChs(v) && eqSip(v) && eqLock(v) && eqVis(v) && eqSav(v));
  if (m) return m;
  const sameStruct = vs.filter((v) => eqNet(v) && eqHsa(v) && eqTier(v) && eqTerm(v) && eqCiB(v) && eqChs(v) && eqSip(v) && eqLock(v) && eqVis(v) && eqSav(v));
  if (sameStruct.length) return nearest(sameStruct);
  const sameNet = vs.filter((v) => eqNet(v) && eqTier(v) && eqTerm(v) && eqCiB(v) && eqChs(v) && eqSip(v) && eqLock(v) && eqVis(v) && eqSav(v));
  if (sameNet.length) return nearest(sameNet);
  // Dual-benefit-axis fallback (UHOne ProGuard): if BOTH benefit axes are constrained and the
  // exact cell is missing (a sparse accident×CI matrix), keep the primary accident `tier` and
  // snap the CI axis — so a chip click never dead-ends. Scoped to ciBenefitLevel-bearing targets,
  // so it has zero effect on every other product. (Live ProGuard is a full 2×3; this is defensive.)
  if (target.ciBenefitLevel != null) {
    const keepTier = vs.filter((v) => eqTier(v));
    if (keepTier.length) return nearest(keepTier);
    const keepCi = vs.filter((v) => eqCiB(v));
    if (keepCi.length) return nearest(keepCi);
  }
  return null;
}

/** Group key for a single plan — must match groupPlanVariants' key exactly. */
export function groupKeyFor(plan) {
  const { family, term } = deriveVariantAxes(plan);
  const carrier = (plan && (plan.carrier || plan.network)) || 'LifeX';
  const type = normalizeGroupType((plan && (plan.type || plan.category)) || '', family, term);
  return carrier + '|' + type + '|' + family;
}

/**
 * Level values (deductible/coinsurance) that have an exact variant for the current
 * structural selection (network + hsa + tier). Drives the gray-out: a level chip whose
 * value isn't in the returned set has no matching variant and should be disabled.
 */
export function availableLevelValues(group, current) {
  const cur = current || {};
  const eqNet = (v) => cur.network == null || v.network === cur.network;
  const eqHsa = (v) => cur.hsa == null || !!v.hsa === !!cur.hsa;
  const eqTier = (v) => cur.tier == null || v.tier === cur.tier;
  const eqTerm = (v) => cur.term == null || v.term === cur.term;
  const eqCiB = (v) => cur.ciBenefitLevel == null || v.ciBenefitLevel === cur.ciBenefitLevel;
  const eqChs = (v) => cur.chs == null || v.chs === cur.chs;
  const eqSip = (v) => cur.sip == null || v.sip === cur.sip;
  // Same null-tolerant rate-lock form as findVariantByAxes (null-axis variants always pass).
  const eqLock = (v) => cur.rateLock == null || v.rateLock == null || v.rateLock === cur.rateLock;
  const eqVis = (v) => cur.visionRider == null || v.visionRider === cur.visionRider;
  const eqSav = (v) => cur.savingsCard == null || v.savingsCard === cur.savingsCard;
  const deductibles = new Set();
  const coinsurances = new Set();
  for (const v of (group && group.variants) || []) {
    if (eqNet(v) && eqHsa(v) && eqTier(v) && eqTerm(v) && eqCiB(v) && eqChs(v) && eqSip(v) && eqLock(v) && eqVis(v) && eqSav(v)) {
      deductibles.add(v.deductible);
      if (v.coinsurance) coinsurances.add(v.coinsurance);
    }
  }
  return { deductibles, coinsurances };
}

/** Find a variant by deductible + coinsurance; null if that combo wasn't returned. */
export function findVariant(group, deductible, coinsurance) {
  return group.variants.find(v =>
    v.deductible === deductible && (coinsurance == null || v.coinsurance === coinsurance)
  ) || null;
}

// --- Optional rider toggles (#4) ---
// Riders are NOT a variant axis: they're boolean, additive and independent (an overlay on the
// selected variant), so effective premium = base variant premium + Σ active rider premiums.
// Manhattan exposes them on plan.planDetails.riders=[{riderName,riderUnitId,riderType,premium}];
// only Optional riders that carry a premium are user-toggleable.

/** Optional, priced riders available on a variant — for rendering the rider toggle chips. The
 *  defaultOn flag marks riders that should start toggled ON (e.g. Allstate STM Recuro Rx/Behavioral
 *  Health, #5) vs opt-in riders like Manhattan DVH Vision/Hearing. */
export function optionalRiders(variant) {
  const riders = (variant && variant.plan && variant.plan.planDetails && variant.plan.planDetails.riders) || [];
  return riders
    .filter((r) => r && r.riderType === 'Optional' && r.premium != null)
    .map((r) => ({ riderUnitId: r.riderUnitId, riderName: r.riderName, premium: Number(r.premium) || 0, defaultOn: !!r.defaultOn }));
}

/** Unit ids of a variant's riders that should be active by default (defaultOn). */
export function defaultActiveRiders(variant) {
  return new Set(optionalRiders(variant).filter((r) => r.defaultOn).map((r) => r.riderUnitId));
}

/** The subset of a variant's optional riders whose unit ids are in the active set/array. */
export function activeRidersFor(variant, activeUnitIds) {
  const active = activeUnitIds instanceof Set ? activeUnitIds : new Set(activeUnitIds || []);
  return optionalRiders(variant).filter((r) => active.has(r.riderUnitId));
}

/** Effective monthly premium = base variant premium + active optional-rider premiums. */
export function effectivePremium(variant, activeUnitIds) {
  const base = Number(variant && variant.premium) || 0;
  return base + activeRidersFor(variant, activeUnitIds).reduce((s, r) => s + r.premium, 0);
}
